// Klaviyo `_kx` capture.
//
// Klaviyo appends `_kx=<token>` to every link it sends in an email. That token
// identifies the browser profile it was sent to, which is what lets a
// SERVER-side event (Started Checkout, Placed Order) attach to the right person
// before we have ever seen their email address — the whole point of
// `klaviyoapi.Profile.Kx`, which every event builder in
// internal/domain/klaviyosync already reads off `tracking_context.kx`.
//
// Captured at LANDING, not at checkout. matchsignals.ts reads its other values
// straight from the URL because a click id is still there when the buyer pays;
// `_kx` is not — the shopper lands on a product page, browses, and the param is
// long gone by the time /checkout runs. So it has to be persisted on arrival and
// replayed later, which is the same last-touch shape lib/attribution.ts uses for
// utm_*.
//
// The storage key is HOST-SCOPED, unlike attribution.ts's shared
// `dk_attribution`. That is deliberate and not cosmetic: `_kx` names a person,
// so on an origin serving more than one store (dev's `?preview_store=`, or any
// future shared-origin setup) an unscoped key would hand store B the identity of
// store A's shopper. Campaign strings leaking is untidy; an identity leaking is
// a cross-tenant bug.

// Computed per call, not snapshotted at module load — the lazy form lib/visitor.ts
// uses. A module-level const would bake in whatever host was current when the
// chunk first loaded, which is the wrong value for anything that resolves the
// store per tab (dev's ?preview_store=).
const key = () => `dk:klaviyo-kx:${typeof location !== "undefined" ? location.host : ""}`
const PARAM = "_kx"
const MAX_LEN = 512
// Klaviyo's own onsite cookie is long-lived, but a token from a year-old email
// should not credit a fresh purchase to that send. 30 days matches attribution.
const TTL_MS = 30 * 24 * 60 * 60 * 1000

// captureKlaviyoKx reads `_kx` off the landing URL and persists it as the new
// last-touch. Call once on boot, beside captureAttribution(). Safe to call
// repeatedly; a load with no `_kx` leaves any stored value alone, so ordinary
// navigation inside the site does not erase the token the email arrived with.
export function captureKlaviyoKx(): void {
  try {
    const v = (new URLSearchParams(location.search).get(PARAM) ?? "").trim().slice(0, MAX_LEN)
    if (!v) return
    localStorage.setItem(key(), JSON.stringify({ kx: v, ts: Date.now() }))
  } catch {
    // private mode / storage disabled — this is best-effort enrichment.
  }
}

// getKlaviyoKx returns the stored token, or "" when absent or stale.
export function getKlaviyoKx(): string {
  try {
    const raw = localStorage.getItem(key())
    if (!raw) return ""
    const parsed = JSON.parse(raw) as { kx?: unknown; ts?: number }
    if (parsed.ts && Date.now() - parsed.ts > TTL_MS) {
      localStorage.removeItem(key())
      return ""
    }
    return typeof parsed.kx === "string" ? parsed.kx.slice(0, MAX_LEN) : ""
  } catch {
    return ""
  }
}
